import React, { useState, useRef, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";

// Import DND kit
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  defaultAnimateLayoutChanges,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Import hooks and utils
import { useImageManagerUI } from "@/hooks/useImageManagerUI";
import { buildImageContextKey } from "@/utils/image-central-manager/buildContextKey";

// Import components
import CustomDropzoneForm from "@/components/dropzone/CustomDropzoneForm";

// Import inhouse icons
import { Image } from "@/components/icons_new";

const ProductImageUpload = ({ value = [], onChange, error, setError, clearErrors }) => {
  const { openImageManager } = useImageManagerUI();
  const dropzoneRef = useRef(null);
  const params = useParams();
  const location = useLocation();

  const productId = params?.id && params.id !== "new" ? params.id : null;
  const contextKey = buildImageContextKey({
    scope: "product",
    field: "images",
    entityId: productId,
    routeKey: location?.pathname,
  });

  const handleRemove = (indexToRemove) => {
    const updated = value
      .filter((_, i) => i !== indexToRemove)
      .map((img, index) => ({
        ...img,
        role: index === 0 ? "main" : "secondary",
        position: index,
      }));

    onChange(updated);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const getKey = (img, idx) => img?.uiId ?? img?.id ?? String(idx);
    const oldIndex = value.findIndex((img, idx) => getKey(img, idx) === active.id);
    const newIndex = value.findIndex((img, idx) => getKey(img, idx) === over.id);

    const reordered = arrayMove(value, oldIndex, newIndex);

    // Recompute roles and positions so first is always primary/main
    const mapped = reordered.map((img, index) => ({
      ...img,
      role: index === 0 ? "main" : "secondary",
      position: index,
    }));

    onChange(mapped);
  };

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-[#636363] mb-2">Media</label>
      <div className="rounded-md border border-dashed border-gray-300 p-4 bg-[#F6F6F6]">
        <CustomDropzoneForm
          value={value}
          dropzoneRef={dropzoneRef}
          onChange={onChange}
          setError={setError}
          clearErrors={clearErrors}
          triggerFrom="upload"
          showSelectExistingButton={true}
          onSelectExistingClick={() =>
            openImageManager({
              contextKey,
              allowDuplicates: true,
              maxSelect: 5,
              value: value,
              onSelect: (selectedImages) => {
                const newImages = selectedImages.map((img) => ({
                  id: img.id ?? undefined,
                  uiId: crypto.randomUUID(),
                  url: img.url,
                  alt: img.alt,
                  file: null,
                }));
                onChange(newImages);
              },
            })
          }
        />

        {/* Preview Grid with Drag & Drop */}
        {value.length > 0 && (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={value.map((img, i) => img?.uiId ?? img?.id ?? String(i))}
              strategy={rectSortingStrategy}
            >
              <div className="mt-4 grid gap-2 grid-cols-2 sm:grid-cols-4 auto-rows-[96px] sm:auto-rows-[120px]">
                {value.map((img, idx) => (
                  <SortableImage
                    key={img?.uiId ?? img?.id ?? idx}
                    id={img?.uiId ?? img?.id ?? String(idx)}
                    url={img.url}
                    alt={img.alt}
                    isPrimary={idx === 0}
                    onRemove={() => handleRemove(idx)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error.message}</p>}
    </div>
  );
};

export default ProductImageUpload;

// Sortable image card
function SortableImage({ id, url, alt, onRemove, isPrimary = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    animateLayoutChanges: defaultAnimateLayoutChanges,
  });

  const [imageError, setImageError] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`relative group border rounded-md overflow-hidden ${
        isPrimary ? "col-span-2 row-span-2" : "col-span-1 row-span-1"
      }`}
    >
      {/* primary badge removed intentionally */}
      {!url || imageError ? (
        // Empty image fallback
        <div className="flex items-center justify-center w-full h-full bg-[#f3f3f3]">
          <Image className="w-6 h-6 text-[#ccc]" />
        </div>
      ) : (
        <img
          src={url}
          alt={alt || "Image preview"}
          onError={() => setImageError(true)}
          className="object-cover w-full h-full"
        />
      )}

      {/* remove button hidden as requested */}
    </div>
  );
}
